/**
 * StartTransientGame.jsx
 * 
 * Composant principal pour démarrer un jeu temporaire (transient game).
 * Récupère les données du dernier jeu depuis l'API et permet à l'utilisateur
 * de saisir des réponses et de les envoyer.
 */

import React, { useState, useEffect } from 'react';
import styles from './StartTransientGame.module.scss';
import { DEBUG, PATH } from '../../utils/constants';

import AnswerGrid from './AnswerGrid/AnswerGrid';

// APIS 
import { sendGameData } from '../../utils/api/sendGridAPI';
import { getLastInsertedGame } from '../../utils/api/transientGameAPI';

const debug = DEBUG;

/**
 * Composant React pour gérer l'interface d'un jeu temporaire.
 * @component
 * @returns {JSX.Element} L'interface utilisateur du jeu temporaire.
 */
const StartTransientGame = () => {

  const [loading, setLoading] = useState(true); // Chargement des données du jeu
  
  // ---------------------------------STATES--------------------------------------------
  const [gameId, setGameId] = useState(0); // ID du jeu
  const [numberOfBoxes, setNumberOfBoxes] = useState(0); // Nombre de cases/réponses
  const [gameName, setGameName] = useState(""); // Nom du jeu
  const [gameDescription, setGameDescription] = useState(""); // Description du jeu
  const [gameImage, setGameImage] = useState(""); // Image du jeu (lien de l'image)
  const [password, setPassword] = useState(''); // Mot de passe découvert par l'utilisateur
  const [nbPlayers, setNbPlayers] = useState(1); // Nombre de joueurs sur la fiche
  const [jsonOutput, setJsonOutput] = useState(null); // Affichage JSON (debug)

  const [hasHints, setHasHints] = useState(false);
  const [hasPlaces, setHasPlaces] = useState(false);

  const [showHints, setShowHints] = useState(false); // État affichage réponses par défaut (false) | indices (true)

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // ---------------------------------EFFECTS--------------------------------------------
  /**
   * Récupère les données du dernier jeu au montage du composant.
   * Appelle l'API getLastInsertedGame().
   */
  useEffect(() => {
    async function fetchLastGame() {
      try {
        const game = await getLastInsertedGame();
        if (game) {
          setNumberOfBoxes(game.steps);
          setGameName(game.nom);
          setGameId(game.id);
          setGameImage(game.image);
          setGameDescription(game.description);
          setHasHints(game.hints === 1 || game.hints === true); 
          setHasPlaces(game.places === 1 || game.places === true); 
        }
      } catch (error) {
        console.error("Erreur lors de la récupération du dernier jeu :", error);
      } finally {
        setLoading(false);
      }
    }

    fetchLastGame();
  }, []);

  // ---------------------------------EVENTS--------------------------------------------
  /**
   * Récupère les réponses et positions dans localStorage,
   * envoie les données au backend via sendGameData().
   * Vide le localStorage si succès.
   */
  const handleSubmit = async () => {
    const answers = {};
  
    for (let i = 0; i < numberOfBoxes; i++) {
      const answer = localStorage.getItem(`answer_${gameId}_${i}`) || '';
      const location = localStorage.getItem(`location_${gameId}_${i}`) || '';
      
      answers[`case_${i + 1}`] = hasPlaces ? 
      { answer, location } : 
      { answer };
    }
  
    const authToken = localStorage.getItem("authToken") || '';
    const userId = localStorage.getItem("userId") || '';
  
    const payload = {
      gameName,
      password,
      nbPlayers,
      authToken,
      userId,
      answers,
      hasPlaces: hasPlaces ? 1 : 0,
      hasHints: hasHints ? 1 : 0
    };
  
    try {
      const res = await sendGameData(payload);   
  
      if (res && res.success) {
        if (debug) console.log("Effacement des données en mémoire");
        for (let i = 0; i < numberOfBoxes; i++) {
          localStorage.removeItem(`answer_${gameId}_${i}`);
          localStorage.removeItem(`hint_${gameId}_${i}`);
          localStorage.removeItem(`location_${gameId}_${i}`);
        }
        setPassword("");
        setShowSuccessModal(true);   // ✅ succès après nettoyage
      }
    } catch (error) {
      if (debug) console.error("Erreur lors de l'envoi :", error);
    }
  };

  // ---------------------------------LAYOUT--------------------------------------------

  if (loading) return <p>Chargement...</p>;

  return (
    <div className={styles.container}>

      {/* 👇 AJOUT : Bannière image en haut */}
      {gameImage && (
        <div className={styles.banner}>
          <img src={`${PATH}${gameImage}`} alt="Bannière de l'événement" />
        </div>
      )}

      <div className={styles.mainContent}>
        <h2 className={styles.title}>{gameName}</h2>
        <p>{gameDescription}</p>
        <AnswerGrid count={numberOfBoxes} showHints={showHints} gameId={gameId} hasPlaces={hasPlaces} hasHints={hasHints} />

        {hasHints && (
          <div className={styles.toggleHintsContainer}>
            <button onClick={() => setShowHints(!showHints)}>
              {showHints ? "Afficher les réponses" : "Afficher les indices"}
            </button>
          </div>
        )}

        <hr className={styles.hr} />

        <div className={styles.passwordSection}>
          {hasHints && (
            <>
              <label htmlFor="gamePassword">Mot de passe du jeu :</label>
              <input
                id="gamePassword"
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </>
          )}
          <label htmlFor="gamePassword" className={styles.nbPlayers}>Combien de personnes ont participé à ce jeu ?</label>
              <input
                id="gamePassword"
                type="number"
                value={nbPlayers}
                onChange={(e) => setNbPlayers(e.target.value)}
              />
          <button onClick={() => setShowConfirmModal(true)}>Envoyer vos réponses</button>
        </div>

        {jsonOutput && (
          <pre className={styles.jsonPreview}>{jsonOutput}</pre>
        )}
      </div>

      {showConfirmModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <p className={styles.modalText}>Êtes-vous sûr de vouloir soumettre définitivement votre grille de réponses ?</p>
            <div className={styles.modalButtons}>
              <button className={styles.cancel} onClick={() => setShowConfirmModal(false)}>Annuler</button>
              <button className={styles.confirm} onClick={() => {
                setShowConfirmModal(false);
                handleSubmit();
              }}>Oui, envoyer</button>
            </div>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <p className={styles.modalText}>Votre formulaire a bien été envoyé, merci pour votre participation et à bientôt pour de nouvelles aventures !</p>
            <button className={styles.close} onClick={() => setShowSuccessModal(false)}>Fermer</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StartTransientGame;
